/**
 * atelier-study.js — the painterly study reveal.
 *
 * Each project in the gallery hangs as a framed canvas. Choosing one
 * opens its STUDY: a cotton sheet that spreads open like wet paint and
 * carries the project's full, verbatim details — nothing invented,
 * nothing reworded, only the same words the catalogue already prints.
 *
 * Accessibility is the frame this hangs in:
 *   - a real <dialog> with showModal (focus containment + inertness
 *     for free), plus a manual fallback with its own focus trap
 *   - Esc, the painted close button, and the backdrop all close it
 *   - focus returns to the frame that opened it
 *   - the page behind holds still (scroll lock) while it is open
 *   - on small screens the study is a full cotton sheet
 *
 * Contracts (REVISION_BRIEF §2d): dispatches 'atl:study-open' and
 * 'atl:study-close' on document so the cinema can calm its dynamic
 * layer, and calls window.AtelierAudio?.event('reveal') on open.
 */
(() => {
  const boot = (A) => {
    A.register('study', (root) => {
      const dialog = root.querySelector('[data-atl-study]');
      const body = root.querySelector('[data-atl-study-body]');
      const closeBtn = root.querySelector('[data-atl-study-close]');
      const scrim = root.querySelector('[data-atl-study-scrim]');
      const sheet = root.querySelector('[data-atl-study-sheet]');
      const cards = Array.from(root.querySelectorAll('[data-atl-study-card]'));
      if (!dialog || !body || !closeBtn || !cards.length) return () => {};

      const hasModal = typeof dialog.showModal === 'function';
      const reduced = () => !A.motion.allows();

      let openState = false;
      let closing = false;
      let opener = null;
      let closeTimer = null;
      const disposers = [];
      const on = (target, type, fn, opts) => {
        target.addEventListener(type, fn, opts);
        disposers.push(() => target.removeEventListener(type, fn, opts));
      };

      /* ---- Filling the sheet: clone the card's dossier verbatim ---- */
      const fill = (card) => {
        const dossier = card.querySelector('[data-atl-study-dossier]');
        body.textContent = '';
        if (dossier) {
          const copy = dossier.cloneNode(true);
          copy.removeAttribute('hidden');
          copy.removeAttribute('data-atl-study-dossier');
          while (copy.firstChild) body.appendChild(copy.firstChild);
        }
        const titleEl = card.querySelector('[data-atl-study-title]');
        const title = titleEl ? titleEl.textContent.trim() : '';
        dialog.setAttribute('aria-label', title ? 'Study — ' + title : 'Project study');
        return title;
      };

      /* ---- Fallback focus trap (only when showModal is missing) ---- */
      const FOCUSABLE =
        'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
      const trapKeydown = (e) => {
        if (!openState) return;
        if (e.key === 'Escape') {
          e.preventDefault();
          close();
          return;
        }
        if (e.key !== 'Tab') return;
        const nodes = Array.from(dialog.querySelectorAll(FOCUSABLE)).filter(
          (el) => el.offsetParent !== null || el === document.activeElement
        );
        if (!nodes.length) return;
        const first = nodes[0];
        const last = nodes[nodes.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      };

      /* ---- Opening ---- */
      const open = (card) => {
        if (openState || closing) return;
        window.clearTimeout(closeTimer);
        const title = fill(card);
        opener = document.activeElement instanceof HTMLElement ? document.activeElement : card;
        openState = true;
        dialog.classList.remove('is-closing');
        document.documentElement.classList.add('atl-study-open');
        if (hasModal) {
          dialog.showModal();
        } else {
          dialog.setAttribute('open', '');
          dialog.classList.add('is-fallback-open');
          /* Without <dialog> support the element has no implicit
             semantics: name it a modal dialog for the duration. */
          dialog.setAttribute('role', 'dialog');
          dialog.setAttribute('aria-modal', 'true');
          if (scrim) scrim.hidden = false;
          document.addEventListener('keydown', trapKeydown, true);
        }
        if (sheet) sheet.scrollTop = 0;
        closeBtn.focus({ preventScroll: true });
        document.dispatchEvent(new CustomEvent('atl:study-open'));
        window.AtelierAudio?.event('reveal');
        if (title) A.helpers.announce('Study opened: ' + title);
      };

      /* ---- Closing: a quick reverse spread, then the real close ---- */
      const finishClose = () => {
        closing = false;
        dialog.classList.remove('is-closing');
        if (hasModal) {
          if (dialog.open) dialog.close();
        } else {
          dialog.removeAttribute('open');
          dialog.classList.remove('is-fallback-open');
          dialog.removeAttribute('role');
          dialog.removeAttribute('aria-modal');
          if (scrim) scrim.hidden = true;
          document.removeEventListener('keydown', trapKeydown, true);
          afterClose();
        }
      };

      const afterClose = () => {
        openState = false;
        document.documentElement.classList.remove('atl-study-open');
        document.dispatchEvent(new CustomEvent('atl:study-close'));
        if (opener && document.contains(opener)) opener.focus({ preventScroll: true });
        opener = null;
      };

      const close = () => {
        if (!openState || closing) return;
        closing = true;
        if (reduced()) {
          finishClose();
          return;
        }
        dialog.classList.add('is-closing');
        closeTimer = window.setTimeout(finishClose, 330);
      };

      /* Native close (Esc via cancel, or anything else): keep state
         honest even if the paint-out was skipped. */
      on(dialog, 'cancel', (e) => {
        e.preventDefault();
        close();
      });
      on(dialog, 'close', () => {
        closing = false;
        dialog.classList.remove('is-closing');
        if (openState) afterClose();
      });

      /* Backdrop click: the dialog element itself is the backdrop hit
         area — the sheet inside swallows its own clicks. */
      on(dialog, 'pointerdown', (e) => {
        if (e.target === dialog) close();
      });
      if (scrim) on(scrim, 'pointerdown', () => close());
      on(closeBtn, 'click', () => close());

      /* ---- The frames open their study ---- */
      cards.forEach((card) => {
        on(card, 'click', (e) => {
          if (e.target.closest('a')) return; /* real links stay links */
          const btn = e.target.closest('[data-atl-study-open]');
          if (btn || !e.target.closest('button')) open(card);
        });
      });

      /* ---- Teardown (Theme Editor reloads must leave nothing) ---- */
      return () => {
        window.clearTimeout(closeTimer);
        if (openState) {
          document.documentElement.classList.remove('atl-study-open');
          if (hasModal && dialog.open) dialog.close();
          if (!hasModal) {
            dialog.removeAttribute('role');
            dialog.removeAttribute('aria-modal');
          }
          document.removeEventListener('keydown', trapKeydown, true);
          if (scrim) scrim.hidden = true;
          document.dispatchEvent(new CustomEvent('atl:study-close'));
          openState = false;
        }
        disposers.forEach((fn) => fn());
      };
    });
  };

  if (window.Atelier && window.Atelier.__ready) boot(window.Atelier);
  else document.addEventListener('atelier:ready', (e) => boot(e.detail), { once: true });
})();
